import { useState, useMemo, useContext, useEffect } from "react";
import {
  IconButton,
  Stack,
  Typography,
  Button,
  TextField,
  LinearProgress,
  Autocomplete,
  Divider,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InlineUploadFile from "./InlineUploadFile";
import { useConfig } from "@/client/useConfig";
import { useRecoilValue } from "recoil";
import { accessTokenState } from "@/client/state";
import { GenAiContext } from "@/client/context";
import { useNavigate } from "react-router-dom";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SuccessModal from "./tables/SuccessModal";
import DocumentTypeModal from "./tables/DocumentTypeModal";
// import DocumentStatusModal from "./tables/DocumentStatusModal";
import FailureModal from "./tables/FailureModal";

type Props = {
  id?: string;
};

const AddNewAccountData: React.FC<Props> = ({ id }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const accessToken = useRecoilValue(accessTokenState);
  const apiClient = useContext(GenAiContext);

  const [companies, setCompanies] = useState<
    { code_emiten: string; company_name: string; id: string }[]
  >([]);
  const [companyName, setCompanyName] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [documents, setDocuments] = useState<
    { type: string; url: string; filename: string }[]
  >([{ type: "Application Form", url: "", filename: "" }]);

  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [failureModalOpen, setFailureModalOpen] = useState(false);
  const [docTypeModalOpen, setDocTypeModalOpen] = useState(false);
  const [_, setDocStatusModalOpen] = useState(false);
  const [newDocType, setNewDocType] = useState("");
  const [__, setDocStatusData] = useState<Record<string, boolean>>({});

  const documentTypes = [
    "Financial Statements",
    "Company Annual Reports",
    "Sustainability Reports",
    "Legal Document",
  ];

  const fileSpec = useMemo(
    () => ({
      max_files: config?.features?.spontaneous_file_upload?.max_files || 20,
      accept: config?.features?.spontaneous_file_upload?.accept || [],
    }),
    [config]
  );

  // Fetch companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoading(true);
        const response = await apiClient.getAllAccountsNoPagination(
          accessToken
        );
        setCompanies(response?.data || []);
      } catch (err) {
        console.error("Failed to get companies:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCompanies();
  }, [apiClient, accessToken]);

  // Fetch account if editing
  useEffect(() => {
    if (id) {
      const fetchAccount = async () => {
        try {
          setIsLoading(true);
          const response = await apiClient.getAccount(id, accessToken);
          const account = response?.data?.[0];
          if (!account) return;

          setCompanyName(account.company_name || "");
          setCompanyCode(account.kode || "");

          // Map old fields into new documents array
          const loadedDocs: { type: string; url: string; filename: string }[] =
            [];
          if (
            account.application_form_url ||
            account.application_form_filename
          ) {
            loadedDocs.push({
              type: "Application Form",
              url: account.application_form_url || "",
              filename: account.application_form_filename || "",
            });
          }
          if (
            account.financial_statement_url ||
            account.financial_statement_filename
          ) {
            loadedDocs.push({
              type: "Financial Statement",
              url: account.financial_statement_url || "",
              filename: account.financial_statement_filename || "",
            });
          }
          if (account.company_profile_url || account.company_profile_filename) {
            loadedDocs.push({
              type: "Company Profile",
              url: account.company_profile_url || "",
              filename: account.company_profile_filename || "",
            });
          }

          setDocuments(
            loadedDocs.length
              ? loadedDocs
              : [{ type: "Application Form", url: "", filename: "" }]
          );
        } catch (err) {
          console.error("Failed to get account:", err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchAccount();
    }
  }, [id, apiClient, accessToken]);

  const handleAddDocument = () => {
    if (!newDocType) return;
    setDocuments((prev) => [
      ...prev,
      { type: newDocType, url: "", filename: "" },
    ]);
    setDocTypeModalOpen(false);
    setNewDocType("");
  };

  const handleDeleteDocument = async (index: number) => {
    const docToDelete = documents[index];
    if (!docToDelete.filename) {
      setDocuments((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    const fileCategory = docToDelete.type.toLowerCase().replace(/\s+/g, "_");

    const payload: any = {
      company_id: companyId,
      file_category: fileCategory,
      identifier: docToDelete.filename,
    };

    try {
      await apiClient.deleteAccountFile(payload, accessToken);
      setDocuments((prev) => prev.filter((_, i) => i !== index));
    } catch (err) {
      console.error("Failed to delete document:", err);
    }
  };

  const handleUpdateDoc = (index: number, url: string, filename: string) => {
    setDocuments((prev) =>
      prev.map((doc, i) => (i === index ? { ...doc, url, filename } : doc))
    );
  };

  const handleCheckDoc = async () => {
    setIsLoading(true);
    try {
      const checkResponse = await apiClient.documentCheck(
        companyId,
        accessToken || ""
      );

      // Make sure you get the actual document booleans
      const data = checkResponse?.data?.data || checkResponse?.data || {};
      console.log("Document check data:", data);

      // Coerce values to booleans just in case
      const hasMissingDocs = Object.values(data).some((v) => !v);

      if (hasMissingDocs) {
        setDocStatusData(data);
        setDocStatusModalOpen(true);
        return;
      }

      await handleAddOrEdit();
    } catch (err) {
      console.error("Failed to check documents.", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddOrEdit = async () => {
    try {
      // If all documents uploaded, create or edit account
      const payload: any = {
        company_name: companyName,
        company_id: companyId,
        kode: companyCode,
      };

      if (id) {
        await apiClient.editAccount(id, payload, accessToken || "");
      } else {
        await apiClient.createAccount(payload, accessToken || "");
      }

      setModalOpen(true);
    } catch (err) {
      console.error("Failed to create or update account:", err);
    }
  };

  const handleBack = () => navigate("/list-account");
  const handleModalClose = () => {
    setModalOpen(false);
    navigate("/list-account");
  };

  return (
    <Stack sx={{ width: "100%", p: 3 }} direction="column" gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
        <Stack>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {id ? "Edit Data" : "Add New Data"}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
            {id
              ? "Update company details or application information to keep the data accurate and up to date"
              : "Add a new company by filling out the required details and uploading the application form."}
          </Typography>
        </Stack>
      </Stack>

      {isLoading && <LinearProgress />}

      <Divider sx={{ opacity: 0.3 }} />

      <Grid container spacing={2} alignItems="flex-start">
        {/* Row 1: Company */}
        <Grid item xs={12} sm={3}>
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={500}>
              Select Company
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Use the dropdown to select a company. You can type in the company
              name.
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={500}>
              Company Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <Autocomplete
              freeSolo
              options={companies}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.company_name
              }
              value={
                companyCode
                  ? companies.find((c) => c.code_emiten === companyCode) || null
                  : companyName || null
              }
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  setCompanyName(newValue);
                  setCompanyCode("");
                  setCompanyId("");
                } else if (newValue) {
                  setCompanyName(newValue.company_name);
                  setCompanyCode(newValue.code_emiten);
                  setCompanyId(newValue.id);
                } else {
                  setCompanyName("");
                  setCompanyCode("");
                  setCompanyId("");
                }
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "input") setCompanyName(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type Company Name"
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "background.paper" }}
                />
              )}
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ opacity: 0.3 }} />
        </Grid>

        {/* Row 2: Upload - only show if company selected */}
        {companyId && (
          <>
            <Grid item xs={12} sm={3}>
              <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  Upload Document
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Upload documents such as application forms or other required
                  files.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Stack spacing={1}>
                {documents.map((doc, idx) => (
                  <Stack key={idx} spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" fontWeight={500}>
                        Upload {doc.type}
                      </Typography>

                      {idx > 0 && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ color: "error.main", cursor: "pointer" }}
                          onClick={() => handleDeleteDocument(idx)}
                        >
                          <IconButton size="small" color="error">
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                          <Typography variant="body2" fontWeight={500}>
                            Delete
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    <InlineUploadFile
                      fileSpec={fileSpec}
                      fileType={doc.type.toLowerCase().replace(/\s+/g, "_")}
                      setFileUrl={(url) =>
                        handleUpdateDoc(idx, url, documents[idx].filename)
                      }
                      setFilename={(filename) =>
                        handleUpdateDoc(idx, documents[idx].url, filename)
                      }
                      {...(doc.filename && { existingFilename: doc.filename })}
                      companyId={companyId}
                    />
                  </Stack>
                ))}

                <Stack direction="row">
                  <Button
                    variant="text"
                    sx={{
                      textTransform: "none",
                      p: 0,
                      minWidth: "unset",
                      color: "primary.main",
                      fontWeight: 500,
                    }}
                    onClick={() => setDocTypeModalOpen(true)}
                  >
                    + Add More Documents
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ opacity: 0.3 }} />
            </Grid>
          </>
        )}
      </Grid>

      <Stack direction="row" justifyContent="flex-end" gap={1} mt={3} pb={5}>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          variant="contained"
          // onClick={handleCheckDoc}
          onClick={handleAddOrEdit}
          disabled={isLoading}
          sx={{
            color: "secondary.contrastText",
            textTransform: "none",
          }}
        >
          Generate Report
        </Button>
      </Stack>

      <SuccessModal
        open={modalOpen}
        onClose={handleModalClose}
        id={companyId}
      />

      <FailureModal
        open={failureModalOpen}
        onClose={() => setFailureModalOpen(false)}
        onRetry={handleCheckDoc}
      />

      <DocumentTypeModal
        open={docTypeModalOpen}
        onClose={() => setDocTypeModalOpen(false)}
        documentTypes={documentTypes}
        selectedType={newDocType}
        onChange={setNewDocType}
        onSelect={handleAddDocument}
      />

      {/* <DocumentStatusModal
        open={docStatusModalOpen}
        data={docStatusData}
        onClose={() => setDocStatusModalOpen(false)}
        onAddMore={() => {
          setDocStatusModalOpen(false);
        }}
        onContinue={async () => {
          setDocStatusModalOpen(false);
          await handleAddOrEdit();
        }}
      /> */}
    </Stack>
  );
};

export default AddNewAccountData;
